import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { mountEventsFeature } from './modern/EventsFeature';

gsap.registerPlugin(ScrollTrigger);

const isReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const bindCursorLayer = (root, reducedMotion) => {
    if (reducedMotion || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return () => {};

    let disposed = false;
    let cleanup = () => {};

    // Keep React out of the critical bundle and load it only for desktop cursors.
    import('./modern/CursorLayer').then(({ mountCursorLayer }) => {
        if (!disposed) cleanup = mountCursorLayer(root);
    });

    return () => {
        disposed = true;
        cleanup();
    };
};

const bindLivingMedia = (root) => {
    if (!('IntersectionObserver' in window)) return () => {};

    const targets = [
        ['[data-modern-golf-ball]', 'mountGolfBallAction'],
        ['[data-photo-deck]', 'mountPhotoDeck'],
        ['[data-image-trail]', 'mountImageTrail'],
        ['[data-gallery-crossfade]', 'mountGalleryPortalCrossfade'],
        ['[data-photo-gallery]', 'mountPhotoGallery'],
    ];
    const cleanups = [];
    let active = true;
    let modulePromise = null;

    const loadModule = () => {
        if (!modulePromise) modulePromise = import('./modern/LivingMedia');
        return modulePromise;
    };

    const observer = new IntersectionObserver((entries) => {
        entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
            const mountName = entry.target.dataset.modernMount;
            observer.unobserve(entry.target);

            loadModule().then((livingMedia) => {
                if (active && typeof livingMedia[mountName] === 'function') {
                    cleanups.push(livingMedia[mountName](entry.target));
                }
            });
        });
    }, { rootMargin: '360px 0px' });

    targets.forEach(([selector, mountName]) => {
        root.querySelectorAll(selector).forEach((target) => {
            target.dataset.modernMount = mountName;
            observer.observe(target);
        });
    });

    return () => {
        active = false;
        observer.disconnect();
        cleanups.forEach((cleanup) => cleanup());
    };
};

const bindEventsFeature = (root, reducedMotion) => {
    const target = root.querySelector('[data-modern-events]');
    if (!target) return () => {};

    const cleanup = mountEventsFeature(target, { reducedMotion });

    return () => {
        cleanup();
    };
};

const bindAlumniCards = (root) => {
    const cleanups = [];

    root.querySelectorAll('[data-alumni-media]').forEach((media) => {
        const toggle = media.querySelector('[data-alumni-toggle]');
        const overlay = media.querySelector('[data-alumni-overlay]');
        if (!toggle || !overlay) return;

        const memberName = media.closest('.rg-alumni-member')?.querySelector('h3')?.textContent?.trim() || 'ce membre';
        const setOpen = (open) => {
            media.classList.toggle('is-overlay-open', open);
            toggle.setAttribute('aria-expanded', String(open));
            toggle.setAttribute('aria-label', `${open ? 'Masquer' : 'Afficher'} la biographie de ${memberName}`);
            overlay.setAttribute('aria-hidden', String(!open));
        };
        const closeOtherOverlays = () => {
            root.querySelectorAll('[data-alumni-media].is-overlay-open').forEach((otherMedia) => {
                if (otherMedia === media) return;

                otherMedia.classList.remove('is-overlay-open');
                otherMedia.querySelector('[data-alumni-toggle]')?.setAttribute('aria-expanded', 'false');
                otherMedia.querySelector('[data-alumni-overlay]')?.setAttribute('aria-hidden', 'true');
            });
        };
        const toggleOverlay = () => {
            const open = !media.classList.contains('is-overlay-open');
            if (open) closeOtherOverlays();
            setOpen(open);
        };
        const onToggle = (event) => {
            event.stopPropagation();
            toggleOverlay();
        };
        const onToggleKeydown = (event) => {
            if (!['Enter', ' '].includes(event.key) || !media.classList.contains('is-overlay-open')) return;

            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
        };
        const onMediaClick = (event) => {
            if (event.target.closest('[data-alumni-social-link]') || event.target.closest('[data-alumni-toggle]')) return;
            toggleOverlay();
        };
        const onSocialClick = (event) => event.stopPropagation();
        const onDocumentClick = (event) => {
            if (!media.contains(event.target)) setOpen(false);
        };
        const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        const onMouseEnter = () => {
            closeOtherOverlays();
            setOpen(true);
        };
        const onMouseLeave = () => setOpen(false);

        toggle.addEventListener('click', onToggle);
        toggle.addEventListener('keydown', onToggleKeydown);
        media.addEventListener('click', onMediaClick);
        document.addEventListener('click', onDocumentClick);
        if (supportsHover) {
            media.addEventListener('mouseenter', onMouseEnter);
            media.addEventListener('mouseleave', onMouseLeave);
        }
        media.querySelectorAll('[data-alumni-social-link]').forEach((link) => link.addEventListener('click', onSocialClick));
        cleanups.push(() => {
            toggle.removeEventListener('click', onToggle);
            toggle.removeEventListener('keydown', onToggleKeydown);
            media.removeEventListener('click', onMediaClick);
            document.removeEventListener('click', onDocumentClick);
            if (supportsHover) {
                media.removeEventListener('mouseenter', onMouseEnter);
                media.removeEventListener('mouseleave', onMouseLeave);
            }
            media.querySelectorAll('[data-alumni-social-link]').forEach((link) => link.removeEventListener('click', onSocialClick));
        });
    });

    return () => cleanups.forEach((cleanup) => cleanup());
};

const bindNavigation = (root) => {
    const toggle = root.querySelector('[data-modern-nav-toggle]');
    const menuToggles = [...root.querySelectorAll('[data-modern-menu-toggle]')];
    const navigationItems = menuToggles.map((button) => button.closest('.rg-rotary-header__navigation-item'));
    const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    const drawer = root.querySelector('[data-modern-nav-drawer]');
    const backdrop = root.querySelector('[data-modern-nav-backdrop]');
    const megaBackdrop = root.querySelector('[data-modern-mega-backdrop]');
    const closeButton = root.querySelector('[data-modern-nav-close]');
    const drawerSubmenuToggles = [...root.querySelectorAll('[data-modern-drawer-submenu]')];
    let hoverCloseTimer;
    const cancelHoverClose = () => {
        window.clearTimeout(hoverCloseTimer);
        hoverCloseTimer = undefined;
    };
    const closeMenus = () => {
        root.querySelectorAll('[data-modern-menu].is-open').forEach((menu) => menu.classList.remove('is-open'));
        menuToggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        megaBackdrop?.classList.remove('is-open');
        if (megaBackdrop) megaBackdrop.hidden = true;
    };

    const closeDrawer = () => {
        drawer?.classList.remove('is-open');
        backdrop?.classList.remove('is-open');
        if (backdrop) backdrop.hidden = true;
        toggle?.setAttribute('aria-expanded', 'false');
        toggle?.classList.remove('is-active');
        toggle?.setAttribute('aria-label', 'Ouvrir le menu');
        drawer?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('rg-nav-locked');
    };

    const openDrawer = () => {
        if (!toggle || !drawer) return;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (backdrop) {
            backdrop.hidden = false;
            requestAnimationFrame(() => backdrop.classList.add('is-open'));
        }
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('is-active');
        toggle.setAttribute('aria-label', 'Fermer le menu');
        document.body.classList.add('rg-nav-locked');
    };

    const toggleDrawer = () => (drawer?.classList.contains('is-open') ? closeDrawer() : openDrawer());
    const onKeydown = (event) => {
        if (event.key === 'Escape') {
            closeMenus();
            closeDrawer();
        }
    };

    const openMenu = (button) => {
        const menu = document.getElementById(button.getAttribute('aria-controls'));
        if (!menu) return;
        cancelHoverClose();
        closeMenus();
        menu.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        if (megaBackdrop) {
            megaBackdrop.hidden = false;
            requestAnimationFrame(() => megaBackdrop.classList.add('is-open'));
        }
    };

    const menuHandlers = menuToggles.map((button) => {
        const handler = () => {
            const menu = document.getElementById(button.getAttribute('aria-controls'));
            const open = !menu?.classList.contains('is-open');
            if (hoverMedia.matches) {
                openMenu(button);
            } else if (open) {
                openMenu(button);
            } else {
                closeMenus();
            }
        };
        button.addEventListener('click', handler);
        return [button, handler];
    });

    const hoverHandlers = navigationItems.map((item, index) => {
        if (!item) return null;
        const onMouseEnter = () => {
            if (hoverMedia.matches) openMenu(menuToggles[index]);
        };
        const onMouseLeave = () => {
            if (hoverMedia.matches) hoverCloseTimer = window.setTimeout(closeMenus, 120);
        };
        item.addEventListener('mouseenter', onMouseEnter);
        item.addEventListener('mouseleave', onMouseLeave);
        return [item, onMouseEnter, onMouseLeave];
    }).filter(Boolean);

    const drawerSubmenuHandlers = drawerSubmenuToggles.map((button) => {
        const handler = () => {
            const submenu = document.getElementById(button.getAttribute('aria-controls'));
            const open = submenu?.hidden;
            submenu.hidden = !open;
            button.setAttribute('aria-expanded', String(open));
            button.querySelector('span').textContent = open ? '−' : '+';
        };
        button.addEventListener('click', handler);
        return [button, handler];
    });

    const closeMenusOnOutsideClick = (event) => {
        if (!event.target.closest('.rg-rotary-header__navigation-item')) closeMenus();
    };
    document.addEventListener('click', closeMenusOnOutsideClick);
    document.addEventListener('keydown', onKeydown);
    toggle?.addEventListener('click', toggleDrawer);
    closeButton?.addEventListener('click', closeDrawer);
    backdrop?.addEventListener('click', closeDrawer);
    megaBackdrop?.addEventListener('click', closeMenus);
    drawer?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeDrawer));

    return () => {
        cancelHoverClose();
        closeDrawer();
        toggle?.removeEventListener('click', toggleDrawer);
        closeButton?.removeEventListener('click', closeDrawer);
        backdrop?.removeEventListener('click', closeDrawer);
        megaBackdrop?.removeEventListener('click', closeMenus);
        drawer?.querySelectorAll('a').forEach((link) => link.removeEventListener('click', closeDrawer));
        menuHandlers.forEach(([button, handler]) => button.removeEventListener('click', handler));
        hoverHandlers.forEach(([item, onMouseEnter, onMouseLeave]) => {
            item.removeEventListener('mouseenter', onMouseEnter);
            item.removeEventListener('mouseleave', onMouseLeave);
        });
        drawerSubmenuHandlers.forEach(([button, handler]) => button.removeEventListener('click', handler));
        document.removeEventListener('click', closeMenusOnOutsideClick);
        document.removeEventListener('keydown', onKeydown);
    };
};

const bindHeaderScroll = (root) => {
    const header = root.querySelector('[data-modern-header]');
    if (!header) return () => {};

    const movementThreshold = 10;
    const topThreshold = 40;
    const syncHeaderSpace = () => root.style.setProperty('--rg-header-space', `${header.offsetHeight}px`);
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(syncHeaderSpace) : null;
    let lastScrollY = Math.max(0, window.scrollY);
    let accumulatedMovement = 0;
    let direction = 0;

    const menuIsOpen = () => document.body.classList.contains('rg-nav-locked')
        || Boolean(root.querySelector('[data-modern-menu].is-open'));
    const focusIsInHeader = () => header.contains(document.activeElement);
    const setHeaderVisible = (visible) => header.classList.toggle('is-scroll-hidden', !visible);
    const resetMovement = (scrollY = window.scrollY) => {
        lastScrollY = Math.max(0, scrollY);
        accumulatedMovement = 0;
        direction = 0;
    };

    const onScroll = () => {
        const scrollY = Math.max(0, window.scrollY);

        if (scrollY <= topThreshold || menuIsOpen() || focusIsInHeader()) {
            setHeaderVisible(true);
            resetMovement(scrollY);
            return;
        }

        const delta = scrollY - lastScrollY;
        lastScrollY = scrollY;

        if (delta === 0) return;

        const nextDirection = delta > 0 ? 1 : -1;
        if (nextDirection !== direction) {
            direction = nextDirection;
            accumulatedMovement = delta;
        } else {
            accumulatedMovement += delta;
        }

        if (Math.abs(accumulatedMovement) < movementThreshold) return;

        setHeaderVisible(direction < 0);
        accumulatedMovement = 0;
    };
    const onFocusIn = (event) => {
        if (header.contains(event.target)) setHeaderVisible(true);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    root.classList.add('rg-header-scroll-enabled');
    syncHeaderSpace();
    resizeObserver?.observe(header);
    setHeaderVisible(true);

    return () => {
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('focusin', onFocusIn);
        resizeObserver?.disconnect();
        root.classList.remove('rg-header-scroll-enabled');
        root.style.removeProperty('--rg-header-space');
        header.classList.remove('is-scroll-hidden');
    };
};

const bindHeroCarousel = (root, reducedMotion) => {
    const carousel = root.querySelector('[data-hero-carousel]');
    if (!carousel || reducedMotion || carousel.dataset.ready) return () => {};

    const slides = Array.from(carousel.querySelectorAll('[data-hero-slide]'));
    if (slides.length < 2) return () => {};

    carousel.dataset.ready = 'true';
    slides.forEach((slide, index) => {
        gsap.set(slide, { autoAlpha: index === 0 ? 1 : 0, scale: index === 0 ? 1 : 1.025, clipPath: 'inset(0 0% 0 0)' });
    });

    let activeIndex = 0;
    let timer;
    let transition;
    let disposed = false;
    const changeSlide = () => {
        if (disposed) return;
        const previousIndex = activeIndex;
        activeIndex = (activeIndex + 1) % slides.length;
        const currentSlide = slides[previousIndex];
        const nextSlide = slides[activeIndex];

        currentSlide.classList.remove('is-active');
        currentSlide.setAttribute('aria-hidden', 'true');
        nextSlide.classList.add('is-active');
        nextSlide.setAttribute('aria-hidden', 'false');

        transition?.kill();
        transition = gsap.timeline({ defaults: { overwrite: 'auto' } });
        transition
            .to(currentSlide, { autoAlpha: 0, scale: 1.025, clipPath: 'inset(0 12% 0 0)', duration: 0.2, ease: 'power2.in' }, 0)
            .set(nextSlide, { autoAlpha: 0, scale: 1.025, clipPath: 'inset(0 14% 0 0)' }, 0)
            .to(nextSlide, { autoAlpha: 1, scale: 1, clipPath: 'inset(0 0% 0 0)', duration: 0.38, ease: 'power2.out' }, 0.13);
    };
    const stop = () => window.clearInterval(timer);
    const start = () => {
        stop();
        timer = window.setInterval(changeSlide, 1100);
    };
    const onVisibilityChange = () => document.hidden ? stop() : start();

    carousel.addEventListener('pointerenter', stop);
    carousel.addEventListener('pointerleave', start);
    document.addEventListener('visibilitychange', onVisibilityChange);
    start();

    return () => {
        disposed = true;
        stop();
        transition?.kill();
        carousel.removeEventListener('pointerenter', stop);
        carousel.removeEventListener('pointerleave', start);
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };
};

const bindShowreelVolume = (root) => {
    const video = root.querySelector('[data-showreel] .rg-showreel-video');
    const button = root.querySelector('[data-showreel-volume]');
    if (!video || !button) return () => {};

    const syncState = () => {
        const muted = video.muted || video.volume === 0;
        button.classList.toggle('is-muted', muted);
        button.setAttribute('aria-label', muted ? 'Activer le son' : 'Couper le son');
        button.setAttribute('aria-pressed', String(!muted));
    };
    const toggleMute = (event) => {
        event.stopPropagation();
        video.muted = !video.muted;
        if (!video.muted && video.volume === 0) video.volume = 0.7;
        syncState();
        const playback = video.play();
        playback?.catch(() => {
            video.muted = true;
            syncState();
        });
    };

    button.addEventListener('click', toggleMute);
    video.addEventListener('volumechange', syncState);
    syncState();

    return () => {
        button.removeEventListener('click', toggleMute);
        video.removeEventListener('volumechange', syncState);
    };
};

const bindShowreelModal = (root) => {
    const showreel = root.querySelector('[data-showreel]');
    const sectionVideo = showreel?.querySelector('.rg-showreel-video');
    const modal = showreel?.querySelector('[data-showreel-modal]');
    const modalVideo = modal?.querySelector('[data-showreel-modal-video]');
    const closeButton = modal?.querySelector('[data-showreel-modal-close]');
    const playButton = modal?.querySelector('[data-showreel-modal-play]');
    const muteButton = modal?.querySelector('[data-showreel-modal-mute]');
    const progress = modal?.querySelector('[data-showreel-modal-progress]');
    const backdrop = modal?.querySelector('[data-showreel-modal-backdrop]');
    const panel = modal?.querySelector('[data-showreel-modal-panel]');

    if (!showreel || !sectionVideo || !modal || !modalVideo || !closeButton || !playButton || !muteButton || !progress || !backdrop || !panel) return () => {};

    let isOpen = false;
    let wasPlaying = false;
    let previousFocus = null;
    let previousBodyOverflow = '';
    let pendingTime = 0;
    let closeTimer = null;

    const syncPlayState = () => {
        const paused = modalVideo.paused;
        modal.classList.toggle('is-playing', !paused);
        playButton.setAttribute('aria-label', paused ? 'Lire la vidéo' : 'Mettre la vidéo en pause');
        playButton.setAttribute('aria-pressed', String(!paused));
    };

    const syncMuteState = () => {
        const muted = modalVideo.muted || modalVideo.volume === 0;
        modal.classList.toggle('is-muted', muted);
        muteButton.setAttribute('aria-label', muted ? 'Activer le son' : 'Couper le son');
        muteButton.setAttribute('aria-pressed', String(!muted));
    };

    const syncProgress = () => {
        if (Number.isFinite(modalVideo.duration) && modalVideo.duration > 0) {
            progress.value = String((modalVideo.currentTime / modalVideo.duration) * 100);
        }
    };

    const applyPendingTime = () => {
        if (!Number.isFinite(modalVideo.duration) || modalVideo.duration <= 0) return;

        modalVideo.currentTime = Math.min(Math.max(pendingTime, 0), modalVideo.duration);
        syncProgress();
    };

    const openModal = () => {
        if (isOpen) return;

        isOpen = true;
        previousFocus = document.activeElement;
        previousBodyOverflow = document.body.style.overflow;
        wasPlaying = !sectionVideo.paused;
        pendingTime = Number.isFinite(sectionVideo.currentTime) ? sectionVideo.currentTime : 0;

        sectionVideo.pause();
        modalVideo.muted = sectionVideo.muted;
        modalVideo.volume = sectionVideo.volume || 0.7;
        modalVideo.load();
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        applyPendingTime();
        syncMuteState();
        syncPlayState();

        if (closeTimer) window.clearTimeout(closeTimer);
        window.requestAnimationFrame(() => {
            if (!isOpen) return;
            modal.classList.add('is-open');
            closeButton.focus({ preventScroll: true });
        });

        const playback = modalVideo.play();
        playback?.catch(() => syncPlayState());
    };

    const closeModal = () => {
        if (!isOpen) return;

        isOpen = false;
        const currentTime = Number.isFinite(modalVideo.currentTime) ? modalVideo.currentTime : pendingTime;
        modalVideo.pause();
        sectionVideo.currentTime = currentTime;
        sectionVideo.muted = modalVideo.muted;
        sectionVideo.volume = modalVideo.volume;
        sectionVideo.dispatchEvent(new Event('volumechange'));
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = previousBodyOverflow;
        pendingTime = currentTime;

        if (wasPlaying) {
            const playback = sectionVideo.play();
            playback?.catch(() => {});
        }

        closeTimer = window.setTimeout(() => {
            if (isOpen) return;
            modal.hidden = true;
            previousFocus?.focus?.({ preventScroll: true });
        }, 320);
    };

    const onShowreelClick = (event) => {
        if (event.target.closest('[data-showreel-volume]') || event.target.closest('[data-showreel-modal]')) return;
        openModal();
    };
    const onShowreelKeydown = (event) => {
        if (!['Enter', ' '].includes(event.key) || event.target !== showreel) return;
        event.preventDefault();
        openModal();
    };
    const onDocumentKeydown = (event) => {
        if (isOpen && event.key === 'Escape') {
            event.preventDefault();
            closeModal();
        }
    };
    const onCloseClick = (event) => {
        event.stopPropagation();
        closeModal();
    };
    const onBackdropClick = (event) => {
        event.stopPropagation();
        if (event.target === backdrop) closeModal();
    };
    const onPanelClick = (event) => event.stopPropagation();
    const onPlayClick = (event) => {
        event.stopPropagation();
        if (modalVideo.paused) {
            const playback = modalVideo.play();
            playback?.catch(() => {});
        } else {
            modalVideo.pause();
        }
        syncPlayState();
    };
    const onMuteClick = (event) => {
        event.stopPropagation();
        modalVideo.muted = !modalVideo.muted;
        if (!modalVideo.muted && modalVideo.volume === 0) modalVideo.volume = 0.7;
        syncMuteState();
    };
    const onProgressInput = (event) => {
        event.stopPropagation();
        if (!Number.isFinite(modalVideo.duration) || modalVideo.duration <= 0) return;
        modalVideo.currentTime = (Number(event.target.value) / 100) * modalVideo.duration;
    };

    showreel.addEventListener('click', onShowreelClick);
    showreel.addEventListener('keydown', onShowreelKeydown);
    document.addEventListener('keydown', onDocumentKeydown);
    closeButton.addEventListener('click', onCloseClick);
    backdrop.addEventListener('click', onBackdropClick);
    panel.addEventListener('click', onPanelClick);
    playButton.addEventListener('click', onPlayClick);
    muteButton.addEventListener('click', onMuteClick);
    progress.addEventListener('input', onProgressInput);
    modalVideo.addEventListener('loadedmetadata', applyPendingTime);
    modalVideo.addEventListener('timeupdate', syncProgress);
    modalVideo.addEventListener('play', syncPlayState);
    modalVideo.addEventListener('pause', syncPlayState);
    modalVideo.addEventListener('volumechange', syncMuteState);

    return () => {
        closeModal();
        if (closeTimer) window.clearTimeout(closeTimer);
        showreel.removeEventListener('click', onShowreelClick);
        showreel.removeEventListener('keydown', onShowreelKeydown);
        document.removeEventListener('keydown', onDocumentKeydown);
        closeButton.removeEventListener('click', onCloseClick);
        backdrop.removeEventListener('click', onBackdropClick);
        panel.removeEventListener('click', onPanelClick);
        playButton.removeEventListener('click', onPlayClick);
        muteButton.removeEventListener('click', onMuteClick);
        progress.removeEventListener('input', onProgressInput);
        modalVideo.removeEventListener('loadedmetadata', applyPendingTime);
        modalVideo.removeEventListener('timeupdate', syncProgress);
        modalVideo.removeEventListener('play', syncPlayState);
        modalVideo.removeEventListener('pause', syncPlayState);
        modalVideo.removeEventListener('volumechange', syncMuteState);
    };
};

const bindCommunityImpact = (root, reducedMotion) => {
    const target = root.querySelector('[data-modern-community]');
    if (!target) return () => {};

    let active = true;
    let cleanup = () => {};
    const mount = () => import('./modern/CommunityImpact').then(({ mountCommunityImpact }) => {
        if (active) cleanup = mountCommunityImpact(target, { reducedMotion });
    });

    if (!('IntersectionObserver' in window)) {
        mount();
        return () => { active = false; cleanup(); };
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        mount();
    }, { rootMargin: '280px 0px' });
    observer.observe(target);

    return () => {
        active = false;
        observer.disconnect();
        cleanup();
    };
};

const initModernExperience = () => {
    const root = document.querySelector('.rg-scope');
    if (!root) return () => {};

    const reducedMotion = isReducedMotion();
    const loadingRoot = document.querySelector('[data-modern-loading-root]');
    let disposed = false;
    let disposeLoadingScreen = () => {};
    let loadingFallbackTimer = null;
    let loaderDismissed = false;

    if (loadingRoot) {
        const loaderStorageKey = 'rg-loader-full-date';
        const loaderStartedAt = performance.now();
        const today = new Date();
        const todayKey = [
            today.getFullYear(),
            String(today.getMonth() + 1).padStart(2, '0'),
            String(today.getDate()).padStart(2, '0'),
        ].join('-');
        let loaderMode = 'full';

        try {
            if (window.localStorage.getItem(loaderStorageKey) === todayKey) {
                loaderMode = 'express';
            } else {
                window.localStorage.setItem(loaderStorageKey, todayKey);
            }
        } catch (error) {
            // Storage can be disabled in a private browser context. In that case,
            // retain the complete loader rather than risking a flash of content.
            loaderMode = 'full';
        }

        loadingRoot.dataset.loaderMode = loaderMode;
        loadingRoot.classList.add('is-react-mounted');

        const dismissLoader = () => {
            if (loaderDismissed) return;
            loaderDismissed = true;

            if (loadingFallbackTimer) {
                window.clearTimeout(loadingFallbackTimer);
                loadingFallbackTimer = null;
            }

            loadingRoot.classList.add('is-complete');
            loadingRoot.setAttribute('aria-hidden', 'true');
            document.documentElement.classList.remove('rg-loading-active');
            document.body.classList.remove('rg-loading-active');
            root.classList.add('rg-loading-complete');

            // Some WebKit builds retain the Lottie node even after React finishes.
            // Removing the mounted tree is safe after the root has been marked complete.
            window.setTimeout(() => {
                disposeLoadingScreen();
                loadingRoot.replaceChildren();
            }, 0);
        };

        const dismissExpressFallback = () => {
            const dismissAfterMinimum = () => {
                const remaining = Math.max(0, 1300 - (performance.now() - loaderStartedAt));
                loadingFallbackTimer = window.setTimeout(dismissLoader, remaining);
            };

            if (document.readyState === 'complete') {
                dismissAfterMinimum();
            } else {
                window.addEventListener('load', dismissAfterMinimum, { once: true });
            }
        };

        // The Blade fallback is already visible, so loading React never creates a blank first frame.
        import('./modern/ModernLoadingScreen')
            .then(({ mountModernLoadingScreen }) => {
                if (disposed || loadingRoot.classList.contains('is-complete')) return;

                disposeLoadingScreen = mountModernLoadingScreen(loadingRoot, {
                    markUrl: loadingRoot.dataset.modernRotaractMark,
                    minimumDisplayDuration: loaderMode === 'express' ? 1300 : 1600,
                    mode: loaderMode,
                    onComplete: dismissLoader,
                });
            })
            .catch(() => {
                if (loaderMode === 'express') {
                    dismissExpressFallback();
                } else {
                    dismissLoader();
                }
            });

        // Keep this timer active after React mounts: Lottie completion is optional, page access is not.
        if (loaderMode === 'full') {
            loadingFallbackTimer = window.setTimeout(dismissLoader, 7200);
        }
    }
    const disposeNavigation = bindNavigation(root);
    const disposeHeaderScroll = bindHeaderScroll(root);
    const disposeHeroCarousel = bindHeroCarousel(root, reducedMotion);
    const disposeShowreelVolume = bindShowreelVolume(root);
    const disposeShowreelModal = bindShowreelModal(root);
    const disposeCursor = bindCursorLayer(root, reducedMotion);
    const disposeLivingMedia = bindLivingMedia(root);
    const disposeEventsFeature = bindEventsFeature(root, reducedMotion);
    const disposeAlumniCards = bindAlumniCards(root);
    const disposeCommunityImpact = bindCommunityImpact(root, reducedMotion);
    root.classList.toggle('rg-motion-ready', !reducedMotion);

    const context = gsap.context(() => {
        if (reducedMotion) {
            root.querySelectorAll('[data-motion="reveal"]').forEach((element) => element.classList.add('is-visible'));
            return;
        }

        const heroLines = root.querySelectorAll('[data-hero-line]');
        const heroMedia = root.querySelector('[data-hero-carousel]');
        const heroTitle = root.querySelector('.rg-hero-title');
        const heroMeta = root.querySelectorAll('.rg-hero-topline, .rg-hero-foot');
        if (heroLines.length || heroMedia || heroMeta.length) {
            const heroTimeline = gsap.timeline({ defaults: { ease: 'power4.out' } });
            if (heroLines.length) heroTimeline.from(heroLines, { yPercent: 115, duration: 1.05, stagger: 0.13, delay: 0.2 });
            if (heroMedia) heroTimeline.from(heroMedia, { clipPath: 'inset(100% 0 0 0)', duration: 1.15 }, '-=0.72');
            if (heroMeta.length) heroTimeline.from(heroMeta, { autoAlpha: 0, y: 12, duration: 0.55, stagger: 0.08 }, '-=0.55');
        }

        if (heroMedia) {
            gsap.to(heroMedia, {
                xPercent: () => window.matchMedia('(max-width: 760px)').matches ? -6 : -14,
                yPercent: () => window.matchMedia('(max-width: 760px)').matches ? 3 : -2,
                scale: () => window.matchMedia('(max-width: 760px)').matches ? 1.09 : 1.22,
                ease: 'none',
                scrollTrigger: { trigger: '.rg-hero', start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
            });
            if (heroTitle) {
                gsap.to(heroTitle, {
                    xPercent: () => window.matchMedia('(max-width: 760px)').matches ? -3 : -7,
                    yPercent: () => window.matchMedia('(max-width: 760px)').matches ? -8 : -16,
                    scale: () => window.matchMedia('(max-width: 760px)').matches ? 0.97 : 0.92,
                    autoAlpha: 0.36,
                    ease: 'none',
                    scrollTrigger: { trigger: '.rg-hero', start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
                });
            }
        }

        const showreel = root.querySelector('[data-showreel]');
        const showreelMedia = root.querySelector('[data-showreel-media]');
        const showreelTitle = root.querySelector('[data-showreel-title]');
        const showreelNote = root.querySelector('[data-showreel-note]');
        if (showreel && showreelMedia) {
            // Keep the initial reveal dimensions in sync with the responsive CSS.
            // Previously the desktop 64vw/48vh values were written as inline GSAP
            // styles on every viewport, overriding the mobile frame sizing.
            const showreelIsMobile = () => window.matchMedia('(max-width: 760px)').matches;
            const showreelTimeline = gsap.timeline({
                scrollTrigger: {
                    trigger: showreel,
                    start: 'top top',
                    end: () => window.matchMedia('(max-width: 760px)').matches ? 'bottom bottom' : '+=480',
                    scrub: 0.25,
                    invalidateOnRefresh: true,
                },
            });

            showreelTimeline
                .fromTo(showreelMedia, {
                    width: () => showreelIsMobile() ? '88vw' : '64vw',
                    height: () => showreelIsMobile() ? '36svh' : '48vh',
                }, { width: '100vw', height: '100svh', duration: 0.28, ease: 'power3.out' }, 0)
                .to(showreelMedia, { width: '100vw', height: '100svh', duration: 0.72, ease: 'none' }, 0.28);
            if (showreelTitle) showreelTimeline.to(showreelTitle, { yPercent: -25, autoAlpha: 0.2, duration: 1, ease: 'none' }, 0);
            if (showreelNote) showreelTimeline.to(showreelNote, { yPercent: 35, autoAlpha: 0, duration: 1, ease: 'none' }, 0);
        }

        const akaFooter = root.querySelector('[data-aka-footer]');
        if (akaFooter) {
            const footerTitle = akaFooter.querySelector('h2');
            const footerMap = akaFooter.querySelector('.aka-contact-map');
            const footerDetails = akaFooter.querySelector('.aka-contact-details');
            const footerBar = akaFooter.querySelector('footer');
            const footerSequence = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: akaFooter,
                    start: 'top 98%',
                    end: () => window.matchMedia('(max-width: 760px)').matches ? 'top 30%' : 'top 18%',
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            });

            if (footerTitle) footerSequence.fromTo(footerTitle, { xPercent: -10, scale: 0.92, clipPath: 'inset(0 100% 0 0)', autoAlpha: 0 }, { xPercent: 0, scale: 1, clipPath: 'inset(0 0 0 0)', autoAlpha: 1, duration: 0.34 }, 0.08);
            if (footerMap) footerSequence.fromTo(footerMap, { xPercent: 20, autoAlpha: 0 }, { xPercent: 0, autoAlpha: 1, duration: 0.2 }, 0.3);
            if (footerDetails) footerSequence.fromTo(footerDetails, { yPercent: 20, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, duration: 0.18 }, 0.46);
            if (footerBar) {
                footerSequence
                    .fromTo(footerBar, { yPercent: 14, autoAlpha: 0.18, clipPath: 'inset(100% 0 0 0)' }, { yPercent: 7, autoAlpha: 0.68, clipPath: 'inset(0 0 28% 0)', duration: 0.14 }, 0.58)
                    .to(footerBar, { yPercent: 0, autoAlpha: 1, clipPath: 'inset(0 0 0% 0)', duration: 0.16 }, 0.72);
                const footerBrand = footerBar.querySelector('span:first-child');
                const footerSpacer = footerBar.querySelector('span:nth-child(2)');
                const footerCredit = footerBar.querySelector('a');
                if (footerBrand) footerSequence.fromTo(footerBrand, { xPercent: -8, autoAlpha: 0.5 }, { xPercent: 0, autoAlpha: 1, duration: 0.12 }, 0.64);
                if (footerSpacer) footerSequence.fromTo(footerSpacer, { yPercent: 10, autoAlpha: 0.5 }, { yPercent: 0, autoAlpha: 1, duration: 0.12 }, 0.68);
                if (footerCredit) footerSequence.fromTo(footerCredit, { xPercent: 8, autoAlpha: 0.5 }, { xPercent: 0, autoAlpha: 1, duration: 0.12 }, 0.72);
            }
        }

        const actionsTakeover = root.querySelector('[data-actions-takeover]');
        const actionsTakeoverMedia = root.querySelector('[data-actions-takeover-media]');
        if (actionsTakeover && actionsTakeoverMedia) {
            gsap.fromTo(actionsTakeoverMedia,
                { scale: 0.9, xPercent: -12, yPercent: 8 },
                {
                    scale: 1.16,
                    xPercent: 9,
                    yPercent: -7,
                    ease: 'none',
                    scrollTrigger: { trigger: actionsTakeover, start: 'top 82%', end: 'bottom 30%', scrub: true },
                },
            );
        }

        const galleryPortal = root.querySelector('[data-gallery-portal]');
        const galleryPortalMedia = root.querySelector('[data-gallery-portal-media]');
        if (galleryPortal && galleryPortalMedia) {
            gsap.fromTo(galleryPortalMedia,
                { scale: 1.18, yPercent: 7 },
                {
                    scale: 1.48,
                    yPercent: -8,
                    ease: 'none',
                    scrollTrigger: { trigger: galleryPortal, start: 'top bottom', end: 'bottom top', scrub: true },
                },
            );
        }

        gsap.utils.toArray('[data-motion="reveal"]', root).forEach((element) => {
            gsap.fromTo(element,
                { autoAlpha: 0, y: 48 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: element, start: 'top 86%', once: true },
                },
            );
        });

        const actions = root.querySelector('.rg-actions');
        if (actions) {
            gsap.to(actions, {
                '--rg-action-line': 1,
                scrollTrigger: { trigger: actions, start: 'top 75%', end: 'top 35%', scrub: true },
            });
        }

    }, root);

    return () => {
        disposed = true;
        if (loadingFallbackTimer) window.clearTimeout(loadingFallbackTimer);
        context.revert();
        disposeNavigation();
        disposeHeaderScroll();
        disposeHeroCarousel();
        disposeShowreelVolume();
        disposeShowreelModal();
        disposeCursor();
        disposeLivingMedia();
        disposeEventsFeature();
        disposeAlumniCards();
        disposeCommunityImpact();
        disposeLoadingScreen();
    };
};

let cleanupModernExperience = () => {};
document.addEventListener('DOMContentLoaded', () => {
    cleanupModernExperience = initModernExperience();
});
window.addEventListener('pagehide', () => cleanupModernExperience(), { once: true });
window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    cleanupModernExperience();
    cleanupModernExperience = initModernExperience();
});
